import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { CheckCircle2, Circle, ExternalLink, FlaskConical, Loader2, Pencil, Play, Plus, RotateCcw, Search, Sparkles, Ticket, Trash2, X, XCircle } from 'lucide-react'
import Modal from '@/components/common/Modal'
import { setChecked, type JourneyObject, type ObjectStatus, type ScreenTestResult } from '@/api/journey'
import {
  apiError, deleteCase, getCaseResult, getMyAccess, getRun, listCases, raiseTicket, runFileUrl, startRun,
  type CaseResult, type CaseRow, type RunDetail,
} from '@/api/uiTesting'
import { useConfirm } from '@/components/common/ConfirmDialog'
import CaseBuilder from '@/pages/testing/CaseBuilder'
import { askAiToFixCase, Lightbox, Thumb } from '@/pages/testing/shared'
import { isLive } from '@/pages/testing/TestingPage'
import { refreshJourney, useJourneyStore } from '@/store/journeyStore'
import { useJourneyAction } from '@/lib/journeyActions'
import { statusLabel, typeName, TYPE_ICON } from '@/components/workbench/AppTree'
import vocabulary from './vocabulary.json'

/**
 * Checking screens (the journey's Check step). Two ways, one record:
 *  • automatically — the Testing tool runs the screen's UI tests in a real
 *    browser; a pass marks it checked (backend builder_journey/screen_tests);
 *  • by hand — open it, try it, confirm it works (with an optional note).
 * One selected screen → only its tests; "Test all" → every built screen not
 * yet checked.
 */

const fmt = (t?: number) => (t ? new Date(t * 1000).toLocaleString() : '')

/** The screen's status as the builder reads it: a built screen whose latest
 *  automatic test FAILED needs an issue fixed, whatever its journey status. */
export function screenStatus(status: ObjectStatus | null | undefined, test?: ScreenTestResult):
  { cls: string; label: string } | null {
  if (!status) return null
  if (status === 'to_check' && test?.status === 'failed') {
    return { cls: 'failed', label: vocabulary.testStatus.needs_fix }
  }
  return { cls: status, label: statusLabel(status) }
}

/** Never tested automatically: no record to open (only a test to start). */
export const neverTested = (r?: ScreenTestResult) => !r || (!r.run_id && r.status !== 'running')

/** The latest automatic check in one line, with its count — the ONE way into
 *  the screen's test window. Never tested ⇒ a "Test it" button in its place
 *  (it starts the test and opens the window to follow it). */
export function ScreenTestLink({ result, built, running, onOpen, onTest }: {
  result?: ScreenTestResult
  built?: boolean
  /** Some test run is going (only one at a time). */
  running?: boolean
  onOpen: () => void
  /** Start this screen's full test. */
  onTest: () => void
}) {
  if (neverTested(result)) {
    if (!built) return null
    return (
      <button type="button" className="swb-btn swb-btn-secondary" disabled={running}
        onClick={() => { onTest(); onOpen() }} data-tw-action="test-screen"
        title="Test it now in a real browser (the Testing tool). A pass marks it checked.">
        <FlaskConical size={15} /> Test it
      </button>
    )
  }
  const r = result!
  const total = r.total ?? ((r.passed ?? 0) + (r.failed ?? 0))
  const map: Record<string, [string, string]> = {
    running: ['swb-chip-building', 'Testing…'],
    passed: ['swb-chip-checked', `Tests passed${total ? ` · ${r.passed ?? total}/${total}` : ''}`],
    failed: ['swb-chip-failed', `Tests failed${total ? ` · ${r.failed ?? 0} of ${total}` : ''}`],
    fixed: ['swb-chip-to_check', 'Fixed — test again'],
    not_tested: ['swb-chip-neutral', 'No automatic test'],
    error: ['swb-chip-failed', 'Test could not run'],
  }
  const [cls, label] = map[r.status] || ['swb-chip-neutral', r.status]
  return (
    <button type="button" className={`swb-chip ${cls}`} onClick={onOpen} data-tw-action="test-record"
      title={r.message || 'See the tests, run them again, mark it checked'}
      style={{ border: 0, cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2 }}>
      {r.status === 'running' ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <FlaskConical size={12} />}
      {label}
    </button>
  )
}

/** The screen's test window: its latest test record, plus everything a
 *  builder does about it — run the full test, mark it checked (or remove the
 *  mark), add a test of their own and run it. Follows the journey, so a test
 *  started here shows as it runs. */
export function ScreenTestModal({ project, obj, onClose }: {
  project: string
  obj: { obj_type: string; obj_name: string; title: string }
  onClose: () => void
}) {
  const journey = useJourneyStore((s) => s.journey)
  const run = useJourneyAction()
  const [mode, setMode] = useState<'record' | 'confirm' | 'uncheck' | 'build'>('record')
  const [saving, setSaving] = useState(false)
  // Tests a person wrote for this screen (origin "user"), and the one being edited.
  const [mineCases, setMineCases] = useState<CaseRow[]>([])
  const [editCase, setEditCase] = useState<string | undefined>()
  // A run of one hand-written test, followed in the record below.
  const [followRun, setFollowRun] = useState<string | undefined>()
  const [access, setAccess] = useState<{ can_test: boolean; can_fix: boolean }>({ can_test: false, can_fix: false })
  const [caseBusy, setCaseBusy] = useState<string | null>(null)
  const { confirm, confirmDialog } = useConfirm()

  useEffect(() => {
    getMyAccess(project).then(setAccess).catch((err) => {
      // No access answer ⇒ no actions offered; the record itself still shows.
      console.error('[screen-checks] access unavailable', err)
    })
  }, [project])

  const loadMine = useCallback(async () => {
    try {
      const r = await listCases(project)
      setMineCases(r.cases.filter((c) => c.origin === 'user'
        && (c.object || '').toLowerCase() === obj.obj_name.toLowerCase()))
    } catch (err) {
      console.error('[screen-checks] could not list the screen\'s tests', err)
    }
  }, [project, obj.obj_name])
  useEffect(() => { void loadMine() }, [loadMine])

  const runOne = async (caseId: string) => {
    setCaseBusy(`run-${caseId}`)
    try {
      const r = await startRun(project, { trace: 'failed', case_ids: [caseId] })
      toast.success('Running the test')
      setFollowRun(r.run_id)
    } catch (err) {
      toast.error(apiError(err, 'Could not start the test'))
    } finally {
      setCaseBusy(null)
    }
  }

  const removeCase = async (c: CaseRow) => {
    if (!(await confirm({ title: 'Delete the test', message: `Delete “${c.title || c.id}”? Its file is removed from the project; past results stay in the test records.` }))) return
    setCaseBusy(`del-${c.id}`)
    try {
      await deleteCase(project, c.id)
      toast.success('Test deleted')
      await loadMine()
    } catch (err) {
      toast.error(apiError(err, 'Could not delete the test'))
    } finally {
      setCaseBusy(null)
    }
  }
  const j = (journey?.objects || []).find((o) => o.obj_type === obj.obj_type && o.obj_name === obj.obj_name)
  const test = j?.last_test
  const running = !!journey?.tests_running
  const checked = j?.status === 'checked'
  const st = screenStatus(j?.status, test)

  if (mode === 'confirm') return <ConfirmCheckModal project={project} obj={obj} onClose={() => setMode('record')} />
  if (mode === 'build') {
    return (
      <CaseBuilder project={project} obj={obj} caseId={editCase}
        onClose={() => { setEditCase(undefined); setMode('record') }}
        onSaved={() => void loadMine()}
        onRan={(runId) => { setFollowRun(runId); setEditCase(undefined); setMode('record') }} />
    )
  }
  if (mode === 'uncheck') {
    const uncheck = async () => {
      setSaving(true)
      try {
        await setChecked(project, obj.obj_type, obj.obj_name, false)
        toast.success('No longer checked')
        refreshJourney()
        setMode('record')
      } catch (err) {
        const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        toast.error(typeof detail === 'string' && detail ? detail : 'Could not save the check')
      } finally {
        setSaving(false)
      }
    }
    return (
      <Modal open onClose={() => { if (!saving) setMode('record') }} title={`Remove the Checked mark from “${obj.title}”?`}
        footer={<>
          <button type="button" className="btn btn-secondary" disabled={saving} onClick={() => setMode('record')}>Keep it checked</button>
          <button type="button" className="btn btn-primary" disabled={saving} data-tw-action="uncheck-confirm"
            onClick={() => void uncheck()}>{saving ? 'Saving…' : 'Remove the mark'}</button>
        </>}>
        <p style={{ margin: 0 }}>
          {obj.title} was checked
          {j?.check?.method === 'auto' ? ' by an automatic test that passed' : j?.check?.by ? ` by ${j.check.by}` : ''}.
          Removing the mark puts it back to “{statusLabel('to_check')}”.
        </p>
      </Modal>
    )
  }

  const toolbar = (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
      {st && <span className={`swb-chip swb-chip-${st.cls}`}>{st.label}</span>}
      {checked && j?.check && (
        <span className="swb-muted" style={{ fontSize: 13 }}>
          {j.check.method === 'auto' ? 'by a passing test' : `by ${j.check.by || 'someone'}`} {fmt(j.check.at)}
        </span>
      )}
      <span style={{ flex: 1 }} />
      <button type="button" className="swb-btn swb-btn-primary swb-btn-sm" disabled={running || !j?.built}
        onClick={() => void run(`test:${obj.obj_type}:${obj.obj_name}`)} data-tw-action="run-full-test"
        title="Run every test of this screen in a real browser. A pass marks it checked.">
        {test?.status === 'running' ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <FlaskConical size={14} />}
        {test?.status === 'running' ? 'Testing…' : 'Run full test'}
      </button>
      {j?.built && access.can_test && (
        <button type="button" className="swb-btn swb-btn-secondary swb-btn-sm" data-tw-action="add-test"
          onClick={() => { setEditCase(undefined); setMode('build') }}
          title="Write a test of your own, step by step, and run it">
          <Plus size={14} /> Add a test
        </button>
      )}
      {j?.built && (
        <button type="button" className={`swb-btn swb-btn-sm ${checked ? 'swb-btn-quiet' : 'swb-btn-secondary'}`}
          onClick={() => setMode(checked ? 'uncheck' : 'confirm')} data-tw-action="mark-checked"
          title={checked ? 'Checked — click to remove the mark' : 'Tried it and it works? Confirm it.'}>
          {checked ? <CheckCircle2 size={14} /> : <Circle size={14} />}
          {checked ? 'Checked' : 'Mark as checked'}
        </button>
      )}
      {mineCases.length > 0 && (
        <details className="swb-card" style={{ flex: '1 1 100%', padding: '0 14px' }} open={mineCases.length <= 3}>
          <summary style={{ cursor: 'pointer', minHeight: 40, display: 'flex', alignItems: 'center', fontWeight: 600, fontSize: 14 }}>
            Tests you added ({mineCases.length})
          </summary>
          <ul className="swb-list" style={{ paddingBottom: 8 }}>
            {mineCases.map((c) => (
              <li key={c.id} style={{ padding: '6px 0', fontSize: 14 }}>
                <span style={{ flex: '1 1 220px' }}>
                  {c.title || c.id}
                  {c.errors > 0 && <span style={{ color: 'var(--color-error)', fontSize: 13 }}> · needs fixing before it can run</span>}
                </span>
                {access.can_test && (
                  <>
                    <button type="button" className="swb-btn swb-btn-secondary swb-btn-sm" data-tw-action="run-own-test"
                      disabled={!!caseBusy || c.errors > 0} onClick={() => void runOne(c.id)}>
                      <Play size={13} /> {caseBusy === `run-${c.id}` ? 'Starting…' : 'Run'}
                    </button>
                    <button type="button" className="swb-btn swb-btn-quiet swb-btn-sm" data-tw-action="edit-own-test"
                      disabled={!!caseBusy} onClick={() => { setEditCase(c.id); setMode('build') }}>
                      <Pencil size={13} /> Edit
                    </button>
                    <button type="button" className="swb-btn swb-btn-quiet swb-btn-sm" data-tw-action="delete-own-test"
                      aria-label={`Delete ${c.title || c.id}`} disabled={!!caseBusy} onClick={() => void removeCase(c)}>
                      <Trash2 size={13} />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
  return (
    <>
      <TestRecordModal project={project} runId={test?.run_id} objName={obj.obj_name} title={obj.title}
        caseRuns={test?.case_runs} toolbar={toolbar} pending={running || test?.status === 'running'} onClose={onClose}
        followRun={followRun} access={access} />
      {confirmDialog}
    </>
  )
}

/** The record of one automatic check: each test case of the screen, its
 *  outcome, the failure and the step screenshots (the Testing tool's evidence).
 *  A failed case is acted on right here, by role: a developer / project manager
 *  fixes it with AI and re-tests; quality control raises a ticket; anyone who
 *  may test runs that one case again (the window follows the new run). */
export function TestRecordModal({ project, runId, objName, title, onClose, caseRuns, toolbar, pending, followRun, access: givenAccess }: {
  /** Absent: never tested yet — the window shows only the toolbar. */
  project: string; runId?: string; objName: string; title: string; onClose: () => void
  /** A case re-run on its own since this record: case id → the newer run. */
  caseRuns?: Record<string, string>
  /** Shown above the record (the screen's own actions). */
  toolbar?: ReactNode
  /** A test is starting / running though its record is not named yet. */
  pending?: boolean
  /** A run started outside (a hand-written test) — the window follows it. */
  followRun?: string
  /** The caller's rights, when the host already asked. */
  access?: { can_test: boolean; can_fix: boolean }
}) {
  const navigate = useNavigate()
  const [activeRun, setActiveRun] = useState(runId)
  const [query, setQuery] = useState('')
  const [run, setRun] = useState<RunDetail | null>(null)
  const [details, setDetails] = useState<Record<string, CaseResult>>({})
  const [error, setError] = useState<string | null>(null)
  // Index into the visible pictures (all cases' step screenshots, in order).
  const [zoom, setZoom] = useState<number | null>(null)
  const [ownAccess, setAccess] = useState<{ can_test: boolean; can_fix: boolean }>({ can_test: false, can_fix: false })
  const access = givenAccess ?? ownAccess
  const [busy, setBusy] = useState<string | null>(null)
  // A new full test of the screen (started from here or elsewhere) replaces
  // the record shown.
  useEffect(() => {
    setActiveRun(runId)
    setRun(null)
    setDetails({})
  }, [runId])

  useEffect(() => {
    if (!followRun) return
    setActiveRun(followRun)
    setRun(null)
    setDetails({})
  }, [followRun])

  useEffect(() => {
    if (givenAccess) return
    getMyAccess(project).then(setAccess).catch((err) => {
      // No access answer ⇒ no actions offered; the record itself still shows.
      console.error('[screen-checks] access unavailable', err)
    })
  }, [project, givenAccess])

  const live = isLive(run?.status)
  // On the screen's own record, a case re-run since then shows its NEWER
  // result (and evidence); a run followed from here shows itself only.
  const caseRunOf = (caseId: string): string => (activeRun === runId && caseRuns?.[caseId]) || activeRun || ''
  useEffect(() => {
    if (!activeRun) return undefined
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const load = () => getRun(project, activeRun)
      .then(async (r) => {
        if (cancelled) return
        setRun(r)
        setError(null)
        const mine = (r.cases || []).filter((c) => (c.object || '').toLowerCase() === objName.toLowerCase())
        const out: Record<string, CaseResult> = {}
        for (const c of mine) {
          try { out[c.id] = await getCaseResult(project, caseRunOf(c.id), c.id) } catch { /* summary is enough */ }
        }
        if (cancelled) return
        setDetails(out)
        // Follow a run still going (a re-test started from here).
        if (isLive(r.status)) timer = setTimeout(load, 3000)
      })
      .catch((err) => {
        console.error('[screen-checks] test record unavailable', err)
        if (!cancelled) setError('The test record could not be loaded.')
      })
    void load()
    return () => { cancelled = true; if (timer) clearTimeout(timer) }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caseRunOf reads only runId/caseRuns/activeRun
  }, [project, activeRun, objName, runId, caseRuns])

  const runAgain = async (caseId: string) => {
    setBusy(`run-${caseId}`)
    try {
      const r = await startRun(project, { trace: 'failed', case_ids: [caseId] })
      toast.success('Running the test again')
      setRun(null)
      setDetails({})
      setActiveRun(r.run_id)
    } catch (err) {
      toast.error(apiError(err, 'Could not run the test again'))
    } finally {
      setBusy(null)
    }
  }

  const ticket = async (caseId: string, evidenceRun: string = activeRun || '') => {
    setBusy(`ticket-${caseId}`)
    try {
      const r = await raiseTicket(project, evidenceRun, caseId)
      toast.success(r.existing
        ? `${r.ref} is already Support issue #${r.issue}`
        : `Ticket raised — ${r.ref}${r.issue ? ` is Support issue #${r.issue}` : ''}`)
    } catch (err) {
      toast.error(apiError(err, 'Could not raise the ticket'))
    } finally {
      setBusy(null)
    }
  }

  const fix = (caseId: string, caseTitle: string, failure: string, evidenceRun: string = activeRun || '') => {
    askAiToFixCase(project, evidenceRun, caseId, caseTitle, failure, objName)
    toast('Asked AI to fix it and re-run the test — follow it in the Ask AI panel', { icon: '✨' })
    onClose()
  }

  const mine = useMemo(
    () => (run?.cases || []).filter((c) => (c.object || '').toLowerCase() === objName.toLowerCase()),
    [run, objName])
  // Search the page: a test shows when its title, outcome, failure or any of
  // its steps' notes contain every word typed.
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  const shown = useMemo(() => mine.filter((c) => {
    if (!words.length) return true
    const d = details[c.id]
    const hay = [c.title, c.id, c.status, d?.status, c.failure?.message, d?.failure?.message,
      ...(d?.steps || []).flatMap((s) => [s.error, (s as { title?: string }).title, (s as { action?: string }).action])]
      .filter(Boolean).join(' ').toLowerCase()
    return words.every((w) => hay.includes(w))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- words derives from query
  }), [mine, details, query])
  const pictures = useMemo(() => shown.flatMap((c) => {
    const caseRun = caseRunOf(c.id)
    return (details[c.id]?.steps || [])
      .filter((s) => (s as { screenshot?: string }).screenshot)
      .map((s) => {
        const name = (s as { screenshot?: string }).screenshot as string
        return {
          key: `${c.id}/${name}`, caseId: c.id,
          src: runFileUrl(project, caseRun, `cases/${c.id}/${name}`),
          caption: `${c.title || c.id} — step ${s.n}${s.error ? ` — ${s.error}` : ''}`,
          short: `Step ${s.n}${s.error ? ` — ${s.error}` : ''}`,
        }
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- caseRunOf reads only runId/caseRuns/activeRun
  }), [shown, details, project, activeRun, runId, caseRuns])
  const pic = zoom != null ? pictures[zoom] : null

  return (
    <Modal open size="wide" onClose={onClose} title={`Tests — ${title}`}
      footer={<>
        <button type="button" className="btn btn-secondary" onClick={() => navigate(`/testing/${encodeURIComponent(project)}`)}>
          <ExternalLink size={14} /> Open in Testing
        </button>
        <button type="button" className="btn btn-primary" onClick={onClose}>Close</button>
      </>}>
      <div className="swb" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {toolbar}
        {!activeRun ? (
          pending
            ? <p className="swb-muted" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Starting the test…
              </p>
            : <p className="swb-muted" style={{ margin: 0 }}>Not tested yet. Run the full test, or try it yourself and mark it checked.</p>
        ) : error ? <p style={{ color: 'var(--color-error)', margin: 0 }}>{error}</p>
          : !run ? <div className="spinner" style={{ margin: '1.5rem auto' }} />
            : (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
                  <p className="swb-muted" style={{ margin: 0, flex: '1 1 240px' }}>
                    Run {run.run_id} · {live ? `running ${run.done ?? 0}/${run.total ?? 0}` : run.status}
                    {run.browser ? ` · ${run.browser}` : ''}
                    {run.started_by ? ` · started by ${run.started_by}` : ''}
                  </p>
                  {mine.length > 0 && (
                    <label style={{ position: 'relative', flex: '0 1 280px', minWidth: 180 }}>
                      <Search size={14} aria-hidden="true" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-secondary)' }} />
                      <input type="search" value={query} onChange={(e) => { setQuery(e.target.value); setZoom(null) }}
                        placeholder="Search the tests…" aria-label="Search the tests" data-tw-field="test-search"
                        style={{ width: '100%', paddingLeft: 30, paddingRight: query ? 30 : 10 }} />
                      {query && (
                        <button type="button" className="btn btn-ghost btn-sm" aria-label="Clear the search" onClick={() => setQuery('')}
                          style={{ position: 'absolute', right: 4, top: '50%', transform: 'translateY(-50%)', padding: 4 }}>
                          <X size={14} />
                        </button>
                      )}
                    </label>
                  )}
                </div>
                {words.length > 0 && (
                  <p className="swb-muted" style={{ margin: 0, fontSize: 13 }}>
                    {shown.length} of {mine.length} test{mine.length === 1 ? '' : 's'} match “{query.trim()}”
                  </p>
                )}
                {activeRun !== runId && (
                  <p className="swb-muted" style={{ margin: 0 }}>
                    A single test re-run does not change the screen’s Checked mark — use Run full test for that.
                  </p>
                )}
                {mine.length === 0 && <p className="swb-muted">{live ? 'Waiting for the test to start…' : 'No test of this screen ran in this record.'}</p>}
                {shown.map((c) => {
                  const d = details[c.id]
                  const caseRun = caseRunOf(c.id)
                  const rerun = caseRun !== activeRun
                  const status = (rerun && d?.status) || c.status
                  const shots = pictures.map((p, i) => ({ ...p, i })).filter((p) => p.caseId === c.id)
                  const ok = status === 'passed'
                  const failedCase = status === 'failed'
                  const notPassed = failedCase || status === 'blocked'
                  const failure = ((rerun && d ? d.failure : c.failure)?.message) || ''
                  return (
                    <div key={c.id} className="swb-card" style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {ok ? <CheckCircle2 size={17} style={{ color: 'var(--color-success)' }} />
                          : <XCircle size={17} style={{ color: c.status === 'skipped' ? 'var(--color-text-secondary)' : 'var(--color-error)' }} />}
                        <strong style={{ flex: 1, fontSize: 14 }}>{c.title || c.id}</strong>
                        <span className="swb-muted">{status}</span>
                      </div>
                      {rerun && (
                        <p className="swb-muted" style={{ margin: 0, fontSize: 13 }}>
                          Re-run on its own in {caseRun} — that result is shown here.
                        </p>
                      )}
                      {failure && (
                        <div className="swb-notice swb-notice-warn" style={{ padding: '8px 12px', fontSize: 13 }}>
                          Step {c.failure?.step}: {failure}
                        </div>
                      )}
                      {shots.length > 0 && (
                        <div style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
                          {shots.map((p) => (
                            <Thumb key={p.key} src={p.src} caption={p.short} width={140} onOpen={() => setZoom(p.i)} />
                          ))}
                        </div>
                      )}
                      {notPassed && !live && access.can_test && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {access.can_fix && (
                            <button type="button" className="swb-btn swb-btn-primary swb-btn-sm"
                              data-tw-action="fix-case" onClick={() => fix(c.id, c.title || c.id, failure, caseRun)}>
                              <Sparkles size={14} /> Fix with AI and re-test
                            </button>
                          )}
                          {failedCase && !access.can_fix && (
                            <button type="button" className="swb-btn swb-btn-primary swb-btn-sm"
                              data-tw-action="raise-ticket" disabled={!!busy} onClick={() => void ticket(c.id, caseRun)}>
                              <Ticket size={14} /> {busy === `ticket-${c.id}` ? 'Raising…' : 'Raise a ticket'}
                            </button>
                          )}
                          <button type="button" className="swb-btn swb-btn-secondary swb-btn-sm"
                            data-tw-action="rerun-case" disabled={!!busy} onClick={() => void runAgain(c.id)}>
                            <RotateCcw size={14} /> {busy === `run-${c.id}` ? 'Starting…' : 'Run this test again'}
                          </button>
                        </div>
                      )}
                    </div>
                  )
                })}
              </>
            )}
      </div>
      {pic && zoom != null && (
        <Lightbox src={pic.src} caption={pic.caption} onClose={() => setZoom(null)}
          position={pictures.length > 1 ? `${zoom + 1} of ${pictures.length}` : undefined}
          onPrev={zoom > 0 ? () => setZoom(zoom - 1) : undefined}
          onNext={zoom < pictures.length - 1 ? () => setZoom(zoom + 1) : undefined} />
      )}
    </Modal>
  )
}

/** "I tried it and it works" — the manual check, with an optional note. */
export function ConfirmCheckModal({ project, obj, onClose }: {
  project: string; obj: { obj_type: string; obj_name: string; title: string }; onClose: () => void
}) {
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const save = async () => {
    setSaving(true)
    try {
      await setChecked(project, obj.obj_type, obj.obj_name, true, note.trim() || undefined)
      toast.success('Marked as checked')
      refreshJourney()
      onClose()
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' && detail ? detail : 'Could not save the check')
    } finally {
      setSaving(false)
    }
  }
  return (
    <Modal open onClose={() => { if (!saving) onClose() }} title={`Confirm “${obj.title}” works`}
      footer={<>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
        <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Yes, it works'}
        </button>
      </>}>
      <p style={{ marginTop: 0 }}>
        You opened it and tried it. Confirming marks it checked; your name and the time are kept in the
        check record. If its design changes later, it needs checking again.
      </p>
      <label htmlFor="check-note" style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
        What did you try? (optional)
      </label>
      <textarea id="check-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="Added a record, edited it, approved it…" />
    </Modal>
  )
}

/** The right panel when no screen is selected: check the app's screens. */
export function CheckScreensPanel({ project, noun, nouns, types, onOpen, onAdd }: {
  project: string
  noun: string
  nouns: string
  types?: string[] | null
  onOpen: (o: JourneyObject) => void
  onAdd: () => void
}) {
  const journey = useJourneyStore((s) => s.journey)
  const run = useJourneyAction()
  const [record, setRecord] = useState<JourneyObject | null>(null)
  const objects = (journey?.objects || []).filter((o) => !types || types.includes(o.obj_type))
  const built = objects.filter((o) => o.built)
  const toCheck = built.filter((o) => o.status !== 'checked')
  const running = !!journey?.tests_running

  return (
    <div className="swb" style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '4px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: '1 1 320px' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>Check your {nouns}</h2>
          <p className="swb-muted" style={{ margin: '4px 0 0', fontSize: '0.9375rem' }}>
            Twasta tests each {noun} in a real browser with the Testing tool — anything that passes is marked
            checked and its test record is kept. Or pick one on the left, try it and confirm it works.
          </p>
        </div>
        <button type="button" className="swb-btn swb-btn-primary" disabled={running || toCheck.length === 0}
          onClick={() => void run('test:all')} data-tw-action="test-all">
          {running ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <FlaskConical size={15} />}
          {running ? 'Testing…' : toCheck.length ? `Test ${toCheck.length} automatically` : 'All checked'}
        </button>
        <button type="button" className="swb-btn swb-btn-secondary" onClick={onAdd}><Plus size={15} /> Add {noun}</button>
      </div>

      {objects.length === 0 ? (
        <p className="swb-muted">Nothing here yet.</p>
      ) : (
        <ul className="swb-list swb-card" style={{ overflow: 'hidden' }}>
          {objects.map((o) => {
            const Icon = TYPE_ICON[o.obj_type] || TYPE_ICON.T
            return (
              <li key={`${o.obj_type}:${o.obj_name}`}>
                <Icon size={17} style={{ color: 'var(--color-accent)', flex: 'none' }} />
                <button type="button" className="swb-link" style={{ flex: '1 1 180px', textAlign: 'left', textDecoration: 'none', color: 'var(--color-text)' }}
                  onClick={() => onOpen(o)}>
                  {o.title}
                  <span className="swb-muted" style={{ display: 'block', fontWeight: 400 }}>
                    {typeName(o.obj_type)}
                    {o.check ? ` · checked ${o.check.method === 'auto' ? 'by test' : `by ${o.check.by || 'someone'}`} ${fmt(o.check.at)}` : ''}
                  </span>
                </button>
                <ScreenTestLink result={o.last_test} built={o.built} running={running}
                  onOpen={() => setRecord(o)} onTest={() => void run(`test:${o.obj_type}:${o.obj_name}`)} />
                {(() => {
                  const st = screenStatus(o.status, o.last_test)
                  return st && <span className={`swb-chip swb-chip-${st.cls}`}>{st.label}</span>
                })()}
              </li>
            )
          })}
        </ul>
      )}

      {(journey?.check_log?.length ?? 0) > 0 && (
        <details className="swb-card" style={{ padding: '0 16px' }}>
          <summary style={{ cursor: 'pointer', minHeight: 48, display: 'flex', alignItems: 'center', fontWeight: 600 }}>
            Check record
          </summary>
          <ul className="swb-list" style={{ paddingBottom: 8 }}>
            {journey!.check_log.slice(0, 30).map((e, i) => (
              <li key={i} style={{ padding: '8px 0', fontSize: 13 }}>
                <span style={{ flex: '0 0 150px' }} className="swb-muted">{fmt(e.at)}</span>
                <span style={{ flex: '1 1 200px' }}>
                  <strong>{e.obj.split(':').slice(1).join(':')}</strong> — {e.method === 'auto' ? 'automatic test' : 'checked by hand'}: {e.result}
                  {e.by ? ` · ${e.by}` : ''}{e.message ? ` · ${e.message}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {record && (
        <ScreenTestModal project={project} obj={{ obj_type: record.obj_type, obj_name: record.obj_name, title: record.title }}
          onClose={() => setRecord(null)} />
      )}
    </div>
  )
}
